/**
 * TenantCreateModal Component
 * Modal for creating a new tenant with admin user
 */

import React, { useState } from 'react';
import { Building2, User, Lock, Eye, EyeOff } from 'lucide-react';
import { Modal, Button, Input, Select } from '../../../components/common';
import { useCreateTenant } from '../../../hooks/useSuperAdmin';
import type { TenantPlan, CreateTenantRequest } from '../../../types/superadmin';

interface TenantCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const TenantCreateModal: React.FC<TenantCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    organizationName: '',
    plan: 'trial' as TenantPlan,
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    adminPassword: '',
    confirmPassword: '',
    industry: '',
    companySize: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createTenantMutation = useCreateTenant();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
    } else if (formData.organizationName.length < 2) {
      newErrors.organizationName = 'Organization name must be at least 2 characters';
    }

    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Invalid email address';
    }

    if (!formData.adminFirstName.trim()) {
      newErrors.adminFirstName = 'First name is required';
    }

    if (!formData.adminLastName.trim()) {
      newErrors.adminLastName = 'Last name is required';
    }

    if (!formData.adminPassword) {
      newErrors.adminPassword = 'Password is required';
    } else if (formData.adminPassword.length < 8) {
      newErrors.adminPassword = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.adminPassword)) {
      newErrors.adminPassword = 'Password must contain an uppercase letter';
    } else if (!/[a-z]/.test(formData.adminPassword)) {
      newErrors.adminPassword = 'Password must contain a lowercase letter';
    } else if (!/[0-9]/.test(formData.adminPassword)) {
      newErrors.adminPassword = 'Password must contain a number';
    } else if (!/[^A-Za-z0-9]/.test(formData.adminPassword)) {
      newErrors.adminPassword = 'Password must contain a special character';
    }

    if (formData.adminPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const request: CreateTenantRequest = {
      organizationName: formData.organizationName.trim(),
      plan: formData.plan,
      adminUser: {
        email: formData.adminEmail.toLowerCase().trim(),
        firstName: formData.adminFirstName.trim(),
        lastName: formData.adminLastName.trim(),
        password: formData.adminPassword,
      },
      settings: {
        industry: formData.industry || undefined,
        companySize: formData.companySize || undefined,
      },
    };

    try {
      await createTenantMutation.mutateAsync(request);
      // Reset form
      setFormData({
        organizationName: '',
        plan: 'trial',
        adminEmail: '',
        adminFirstName: '',
        adminLastName: '',
        adminPassword: '',
        confirmPassword: '',
        industry: '',
        companySize: '',
      });
      onSuccess();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Tenant" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organization Details */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-stone-700 font-medium">
            <Building2 className="w-5 h-5" />
            Organization Details
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Organization Name *
            </label>
            <Input
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              placeholder="Acme Corporation"
              error={errors.organizationName}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Plan"
              name="plan"
              value={formData.plan}
              onChange={handleChange}
              required
              options={[
                { value: 'trial', label: 'Trial (30 days)' },
                { value: 'basic', label: 'Basic' },
                { value: 'professional', label: 'Professional' },
                { value: 'enterprise', label: 'Enterprise' },
              ]}
            />

            <Input
              label="Industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              placeholder="Technology, Finance, etc."
            />
          </div>

          <Select
            label="Company Size"
            name="companySize"
            value={formData.companySize}
            onChange={handleChange}
            placeholder="Select size"
            options={[
              { value: '1-10', label: '1-10 employees' },
              { value: '11-50', label: '11-50 employees' },
              { value: '51-200', label: '51-200 employees' },
              { value: '201-500', label: '201-500 employees' },
              { value: '500+', label: '500+ employees' },
            ]}
          />
        </div>

        {/* Admin User Details */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center gap-2 text-stone-700 font-medium">
            <User className="w-5 h-5" />
            Admin User Details
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                First Name *
              </label>
              <Input
                name="adminFirstName"
                value={formData.adminFirstName}
                onChange={handleChange}
                placeholder="John"
                error={errors.adminFirstName}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Last Name *
              </label>
              <Input
                name="adminLastName"
                value={formData.adminLastName}
                onChange={handleChange}
                placeholder="Doe"
                error={errors.adminLastName}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Email *
            </label>
            <Input
              name="adminEmail"
              type="email"
              value={formData.adminEmail}
              onChange={handleChange}
              placeholder="admin@company.com"
              error={errors.adminEmail}
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center gap-2 text-stone-700 font-medium">
            <Lock className="w-5 h-5" />
            Set Password
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <Input
                name="adminPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.adminPassword}
                onChange={handleChange}
                placeholder="Min 8 chars, upper, lower, number, special"
                error={errors.adminPassword}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Confirm Password *
            </label>
            <Input
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              error={errors.confirmPassword}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={createTenantMutation.isPending}
          >
            {createTenantMutation.isPending ? 'Creating...' : 'Create Tenant'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
